import { EChartsOption } from 'echarts';
import { getMappedColor } from '../helpers/getColor';
import {
    CartesianChartOption,
    CartesianChartType,
} from './CartesianChartOption';
import { EChartsSerie } from './types';
import { getLegendWidth } from 'helpers/chart/legendHelpers';
import { POLAR_OFFSET } from 'helpers/axis/commons';
import { getYMaxValueFormatter } from 'helpers/axis/yAxis';
import {
    ExtractType,
    getValidSettings,
    plainResponseToFloatValue,
} from './helpers';
import { getVisualMapSettings } from 'helpers/chart/visualMap';
import { formatDisplay } from 'helpers/formatHelper';
import { ChartSettingsT, OrderedColumnsT } from 'business/definitions';
import { polarBarSettings } from 'routes/Explore/components/Settings/components/ChartSettingsDrawer/helpers/settingsByViz/polarBar';
import { isDate } from 'helpers/helpers';
import { formatTooltipTitle } from 'helpers/chartHelper';
import { SeriePointT } from '../../types';

const getPolarChartsPositionning = (
    width: number,
    height: number,
    showLegendOnRightSide: boolean,
) =>
    showLegendOnRightSide
        ? {
              center: [(width - (90 + getLegendWidth(width))) * 0.5, '50%'],
              radius: [
                  POLAR_OFFSET,
                  Math.min(height, width - (90 + getLegendWidth(width))) * 0.5 -
                      40 >
                  0
                      ? Math.min(height, width - (90 + getLegendWidth(width))) *
                            0.5 -
                        40
                      : 5,
              ],
          }
        : {
              center: ['50%', height * 0.5 + 25],
              radius: [
                  POLAR_OFFSET,
                  Math.min(height - 50, width) * 0.5 - 50 > 0
                      ? Math.min(height - 50, width) * 0.5 - 50
                      : 5,
              ],
          };

export class PolarChartOption extends CartesianChartOption {
    constructor(
        orderedColumns: OrderedColumnsT,
        settings: ChartSettingsT,
        data: ResponseDataT[],
        width: number,
        height: number,
        dataZoomInterval: [number, number] | undefined,
        isPdfModeActivated: boolean,
        isPngModeActivated: boolean,
        isChartViewedOnDashboard: boolean,
    ) {
        // Delete unhandled settings:
        const properSettings = getValidSettings(settings, polarBarSettings);
        super(
            orderedColumns,
            properSettings,
            data,
            width,
            height,
            dataZoomInterval,
            isPdfModeActivated,
            isPngModeActivated,
            isChartViewedOnDashboard,
        );

        // Data filtering hack specific to polar chart:
        // Filtering out all the unknown date data on the
        // x axis (if the x axis has dates).
        this.filterUnknownDateOnXAxis();
    }

    /**
     * The backend can return date with an "unknown" value.
     * All other graph handle it well, but the polar chart
     * with display a serie "undefined" if there is unknown date
     * on the dimension choosen for the X axis.
     * In order to avoid displaying this, we remove them.
     * Effectively this means that we are not displaying faithfully
     * all the data, which might be an issue.
     * This need to be called before making the dataset.
     *
     * TODO / IMPROVEMENT: This is really bad that we have to
     * do this specific pre-processing of the data to fix a bug
     * specific to the polar chart.
     * This might not also cover all cases, so it really invites
     * other custom hack, which will quickly become unmaintenable.
     * This also means that we are not showing all the data.
     * Ideally, we should decide *for all the graphs* what we want to
     * do with the case of unknown date.
     * For exemple, we might want to display quite literraly a "unknown"
     * date column (instead of undefined).
     * But optimally, this should be handled *before* it reach the graph,
     * in the backend by either filtering out unknown date (especially if
     * they have no data), ot finding (and fixing, if even possible) the
     * the source of the unknown date.
     */
    private filterUnknownDateOnXAxis() {
        if (
            this.orderedColumns.x !== undefined &&
            isDate(this.orderedColumns.x.type)
        ) {
            // Filter data to remove unknown date from x axis.
            this.data = this.data.filter(
                (d) => d[this.orderedColumns.x!.key] !== 'unknown',
            );
        }
    }

    protected getChartType(): CartesianChartType {
        return 'bar';
    }

    protected override getSeries(): EChartsSerie[] {
        let series = super.getSeries();

        // @ts-expect-error [typing]
        series = series.map((s, sidx) => ({
            ...s,
            // IMPORTANT: The encode format is different for polar coordinate chart.
            // It expect angle/radius instead of the cartesian x/y.
            // So we just switch them.
            encode: {
                // @ts-expect-error [typing] echart does not expose the encode property.
                angle: s.encode.y,
                // @ts-expect-error [typing] echart does not expose the encode property.
                radius: s.encode.x,
            },
            coordinateSystem: 'polar',
            label: {
                ...s.label,
                color: 'white',
                textBorderColor: getMappedColor(
                    s.name,
                    sidx,
                    this.getCustomColor(s.name),
                    this.getColor(),
                ),
                textBorderWidth: 4,
                position: 'middle',
                align: 'center',
                rotate: 0,
            },
            itemStyle: {
                borderRadius: 4,
                color: getMappedColor(
                    s.name,
                    sidx,
                    this.getCustomColor(s.name),
                    this.getColor(),
                ),
            },
            barGap: 0.3,
            barMaxWidth: 48,
        }));

        // handle the stacked bars
        if (this.settings.isStackedBars) {
            series = series.map((serie) => ({
                ...serie,
                // @ts-expect-error [typing] echarts think this is a treeserie for some reason.
                stack: serie.encode.radius,
                itemStyle: {
                    // @ts-expect-error [typing]
                    ...serie.itemStyle,
                    borderRadius: 0,
                },
            }));
        }

        return series;
    }

    override getChartOption(): EChartsOption {
        const option = super.getChartOption();

        // IMPORTANT: For polar chart to work with the dataset, we need to
        // modify the axis definition from x/y Axis to angle/radius Axis.

        // @ts-expect-error [typing] the echart type doesn't expose angle axis.
        option['angleAxis'] = option['yAxis'];
        // @ts-expect-error [typing] the echart type doesn't expose radius axis.
        option['radiusAxis'] = option['xAxis'];
        delete option['yAxis'];
        delete option['xAxis'];
        option['polar'] = getPolarChartsPositionning(
            this.width,
            this.height,
            !!this.settings.showLegendOnRightSide,
        );

        // Handle gradiant / visual map.
        if (this.settings.useGradientColor) {
            const visualMap = this.getVisualMap();
            // When we show the visual map, we hide the legend.
            return {
                ...option,
                visualMap,
                legend: {
                    ...option.legend,
                    show: false,
                },
            };
        }
        return option;
    }

    /**
     * For now, we need to force the xAxis (radius) to be 'category' otherwise
     * it creates issues.
     */
    protected override getXAxisType(): 'category' | 'time' | 'value' {
        return 'category';
    }

    protected override getXAxisNameGap(): number {
        return 25;
    }

    protected override getXAxis(): EChartsOption['xAxis'] {
        let xAxis = super.getXAxis();

        const applyCustomProps = (
            axis: ExtractType<EChartsOption['xAxis']>,
        ) => ({
            ...axis,
            nameLocation: 'end',
            axisLine: {
                show: false,
            },
            splitArea: {
                show: true,
            },
            axisTick: {
                show: false,
            },
            axisLabel: {
                // @ts-expect-error [typing] echart does not always expose axisLabel.
                ...axis.axisLabel,
                align: 'right',
            },
        });

        if (Array.isArray(xAxis)) {
            // @ts-expect-error [typing] typescript is bugging on nameLocation which it infer to string
            // even though we use a 'end' literal :shrug:.
            xAxis = xAxis.map((a) => applyCustomProps(a));
        } else {
            // @ts-expect-error [typing] same.
            xAxis = applyCustomProps(xAxis);
        }

        // To make sure that the axis label are drawn on top of the bars.
        xAxis = {
            ...xAxis,
            z: 2,
        };

        return xAxis;
    }

    protected override getYAxis(): EChartsOption['yAxis'] {
        let yAxis = super.getYAxis();

        // We don't handle double axis for polar, so we always only get the first axis.
        if (Array.isArray(yAxis)) {
            yAxis = yAxis[0];
        }

        yAxis = {
            ...yAxis,
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#ccc',
                },
            },
            max: getYMaxValueFormatter(
                true,
                this.orderedColumns.y.type,
                this.settings.goalValue,
            ),
        };

        return yAxis;
    }

    protected getMinMaxValue(): { min: number; max: number } {
        const valueKey = this.orderedColumns.y.key;
        let maxValue = 0;
        let minValue = Number.MAX_VALUE;
        for (const dataset of this.dataset) {
            if (!Object.hasOwn(dataset, 'source')) {
                continue;
            }
            for (const data of dataset.source) {
                const yValue = data[valueKey];
                if (!yValue) {
                    continue;
                }
                const numberValue: number = plainResponseToFloatValue(yValue);
                if (numberValue > maxValue) {
                    maxValue = numberValue;
                }
                if (numberValue < minValue) {
                    minValue = numberValue;
                }
            }
        }
        return { max: maxValue, min: minValue };
    }

    protected getVisualMap() {
        const { min, max } = this.getMinMaxValue();
        return getVisualMapSettings({
            maxValue:
                this.settings.goalValue !== undefined
                    ? this.settings.goalValue
                    : max,
            minValue: min,
            useGradientColor: !!this.settings.useGradientColor,
            reverseGradientColor: !!this.settings.reverseGradientColor,
            showLegend: !!this.settings.showLegend,
            showLegendOnRightSide: !!this.settings.showLegendOnRightSide,
            palette: this.settings.palette || 'manty',
            dimensionKey: this.orderedColumns.y.key,
            useDataset: true,
            formatter: (value: number) =>
                formatDisplay(
                    value,
                    this.orderedColumns.y.type,
                    this.getDecimalPrecision(),
                    this.settings.displayBy,
                ),
        });
    }

    protected override getTooltipTitle(params: SeriePointT[]) {
        const value = params[0].axisValue;
        let title = formatTooltipTitle(
            value,
            this.orderedColumns.x!.type,
            this.orderedColumns.x?.scale,
            this.interval,
            this.treeValueTranslations,
        ).toString();

        // For stacked bars, we also display the sum of all series in the bar.
        if (params.length > 1 && this.settings.isStackedBars) {
            const dimension =
                params[0].dimensionNames[params[0].encode.angle[0]];
            const sum = params
                .map((d) => Number(d.value[dimension]))
                .filter((d) => d) // filter NaN, 0 and undefined
                .reduce((acc, d) => acc + d, 0);
            title = `${title} : ${formatDisplay(
                sum,
                this.orderedColumns.y.type,
                this.getDecimalPrecision(),
                this.settings.displayBy,
            )}`;
        }

        return title;
    }
}
